import React from 'react';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { getAuth } from 'firebase/auth';

import { color, r, radius, space } from '../../theme/tokens';

// RelevanceLegend — the one-line explainer above the rows, with a miniature of
// the ring itself so the reader can connect the sentence to the thing.
//
// It replaces "Customize your stack: Relevance scores of 4.0+ have the
// strongest clinical research evidence", which explained a threshold nobody
// asked about instead of the control on screen. Geometry and copy follow the
// handoff's legend in frames 2 and 3.
//
// The two states say different things because they mean different things:
// logged out, the score is general research and the invitation is to
// personalise it; logged in, it already reflects your goals and profile.

const LEGEND_FILL = 78; // the handoff draws the mini ring part-filled

export const RingGlyph = ({ size = 20 }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: r(size),
      height: r(size),
      borderRadius: '50%',
      background: `conic-gradient(${color.accent} ${LEGEND_FILL}%, ${color.ringTrack} 0)`,
      flex: 'none',
    }}
  />
);

const RelevanceLegend = ({ curatedGoalName = null, isCuratedStack = false }) => {
  const isLoggedIn = !!getAuth().currentUser;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        // Split in two (STACK-541). The description flexes `1 1 18rem`, so on
        // the one-row desktop layout it grows until it meets the login line and
        // `space-between` has no slack left to distribute — the two read as one
        // run-on sentence. `columnGap` is the gap that survives that, and it is
        // a real one from the spacing scale rather than the 9px the wrapped
        // layout wants. `rowGap` keeps that original value, because between two
        // stacked lines on a phone 9px is right and `space.s4` would be a gulf.
        rowGap: r(9),
        columnGap: space.s4,
        marginTop: r(16),
        padding: `${r(11)} ${r(14)}`,
        backgroundColor: color.paper,
        border: `${r(1)} solid ${color.line}`,
        borderRadius: radius.md,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: r(9), flex: '1 1 18rem' }}>
        <RingGlyph />
        <Box sx={{ fontSize: r(12.5), color: color.inkSoft, lineHeight: 1.4 }}>
          {/* "Ring icon:" prefixes both states — it is what ties the sentence to
              the glyph beside it. */}
          <Box component="span" sx={{ fontWeight: 700, color: color.ink }}>
            Ring icon:
          </Box>{' '}
          {isLoggedIn
            ? "The ring shows each supplement's relevance to your health goals and profile, from 0 to 5."
            : /* "based on general research" is load-bearing: signed out, the
                 score has seen nothing about this user, and the sentence must
                 not imply otherwise. */
              "The ring shows each supplement's relevance from 0 to 5, based on general research."}
        </Box>
      </Box>

      {!isLoggedIn && (
        <Box sx={{ fontSize: r(12.5), color: color.inkSoft, flex: 'none' }}>
          Personalize your stack&nbsp;·{' '}
          <MuiLink
            component={RouterLink}
            to={isCuratedStack ? '/create-stack' : '/login'}
            state={
              isCuratedStack
                ? { prefillGoal: curatedGoalName, source: 'curated_stack' }
                : undefined
            }
            sx={{
              color: color.accent,
              fontWeight: 700,
              textDecoration: 'underline',
              textUnderlineOffset: r(2),
            }}
          >
            Log in
          </MuiLink>
        </Box>
      )}
    </Box>
  );
};

export default RelevanceLegend;
